import React from 'react';
import { 
  Play, 
  RefreshCw, 
  Loader2, 
  Power 
} from 'lucide-react';

interface EcadProcessSidebarProps {
  title: string;
  subtitle: string;
  type: 'perfil' | 'topico';
  name: string | undefined;
  totalRoteiros: number;
  pendingCount: number;
  onTriggerAction?: (action: string, detail?: string) => void;

  status: 'idle' | 'running' | 'stopping' | 'stopped';
  setStatus: React.Dispatch<React.SetStateAction<'idle' | 'running' | 'stopping' | 'stopped'>>;
  progress: number;
  setProgress: React.Dispatch<React.SetStateAction<number>>;
  timer: number;
  setTimer: React.Dispatch<React.SetStateAction<number>>;
  logs: string[];
  setLogs: React.Dispatch<React.SetStateAction<string[]>>;
  autoShutdown: boolean;
  setAutoShutdown: React.Dispatch<React.SetStateAction<boolean>>;
  handleStartProcess: () => void;
  handleStopContainers: () => void;
  handleReset: () => void;
}

export function EcadProcessSidebar({
  title,
  subtitle,
  type,
  name = 'Geral',
  totalRoteiros,
  pendingCount,
  onTriggerAction,

  status,
  progress,
  timer,
  autoShutdown,
  setAutoShutdown,
  handleStartProcess,
  handleStopContainers,
  handleReset
}: EcadProcessSidebarProps) {

  const formatTimer = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{
      width: '270px',
      background: '#ffffff',
      border: '1px solid #e2e8f0',
      borderRadius: '12px',
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      flexShrink: 0,
      boxShadow: '0 1px 3px rgba(0,0,0,0.01)',
      position: 'relative'
    }}>
      {/* Header */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a', margin: 0, fontFamily: "'Outfit', sans-serif" }}>
            {title}
          </h3>
          {status !== 'idle' && (
            <span style={{ 
              fontSize: '0.62rem', 
              fontWeight: 700, 
              color: status === 'running' ? '#10b981' : status === 'stopping' ? '#ea580c' : '#ef4444',
              background: status === 'running' ? 'rgba(16,185,129,0.08)' : status === 'stopping' ? 'rgba(234,88,12,0.08)' : 'rgba(239,68,68,0.08)',
              padding: '2px 6px',
              borderRadius: '4px',
              textTransform: 'uppercase'
            }}>
              {status === 'running' ? 'Processando' : status === 'stopping' ? 'Parando' : 'Parado'}
            </span>
          )}
        </div>
        <p style={{ fontSize: '0.7rem', color: '#64748b', margin: '2px 0 0 0' }}>
          {subtitle}
        </p>
      </div>

      {/* Warning Alert if Auto Shutdown is disabled */}
      {!autoShutdown && (
        <div style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          padding: '8px 10px',
          background: '#fef2f2',
          border: '1px solid #fee2e2',
          borderRadius: '8px',
          animation: 'pulse-warning 2s infinite ease-in-out'
        }}>
          {/* Pulsing red warning light */}
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: '#ef4444',
            animation: 'pulse-dot 1s infinite alternate'
          }} />
          <span style={{ fontSize: '0.65rem', color: '#991b1b', fontWeight: 600, lineHeight: '1.3' }}>
            Aviso: Recursos persistidos em GPU/Servidor ativamente.
          </span>
          <style dangerouslySetInnerHTML={{ __html: `
            @keyframes pulse-dot {
              from { transform: scale(0.85); opacity: 0.5; }
              to { transform: scale(1.2); opacity: 1; }
            }
            @keyframes pulse-warning {
              0% { box-shadow: 0 0 0 0px rgba(239, 68, 68, 0.15); }
              50% { box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.25); }
              100% { box-shadow: 0 0 0 0px rgba(239, 68, 68, 0.15); }
            }
          `}} />
        </div>
      )}

      {/* Progress & Stats Card */}
      {(status !== 'idle' || progress > 0) && (
        <div style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '10px',
          padding: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px'
        }}>
          {/* Progress bar */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>
              <span>Progresso</span>
              <span>{progress}%</span>
            </div>
            <div style={{ width: '100%', height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ 
                width: `${progress}%`, 
                height: '100%', 
                background: 'linear-gradient(90deg, #a78bfa, #7c3aed)',
                transition: 'width 0.3s ease-out',
                borderRadius: '3px'
              }} />
            </div>
          </div>

          {/* Timer & Executed Details */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <div style={{ background: '#ffffff', padding: '6px 8px', borderRadius: '6px', border: '1px solid #f1f5f9' }}>
              <span style={{ display: 'block', fontSize: '0.55rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Tempo</span>
              <span style={{ fontSize: '0.82rem', fontWeight: 800, color: '#475569' }}>
                {formatTimer(timer)}
              </span>
            </div>
            <div style={{ background: '#ffffff', padding: '6px 8px', borderRadius: '6px', border: '1px solid #f1f5f9' }}>
              <span style={{ display: 'block', fontSize: '0.55rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Roteiros</span>
              <span style={{ fontSize: '0.82rem', fontWeight: 800, color: '#475569' }}>
                {totalRoteiros}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Actions and Controls */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {status !== 'running' && status !== 'stopping' ? (
          <button
            onClick={handleStartProcess}
            style={{
              background: '#7c3aed',
              color: '#ffffff',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '8px',
              fontSize: '0.75rem',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.15s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#6d28d9'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#7c3aed'}
          >
            <Play size={14} /> Processar {type === 'perfil' ? 'Perfil' : 'Tópico'} ({totalRoteiros})
          </button>
        ) : (
          <button
            disabled
            style={{
              background: '#e2e8f0',
              color: '#94a3b8',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '8px',
              fontSize: '0.75rem',
              fontWeight: 700,
              cursor: 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <Loader2 size={14} className="spin" /> Processando Lote...
          </button>
        )}

        <button
          onClick={() => onTriggerAction?.('processa-update', name)}
          style={{
            background: '#f8fafc',
            color: '#475569',
            border: '1px solid #e2e8f0',
            padding: '8px 12px',
            borderRadius: '8px',
            fontSize: '0.75rem',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.15s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#f8fafc'}
        >
          <RefreshCw size={14} /> Atualizações Pendentes
        </button>

        {status === 'stopped' && (
          <button
            onClick={handleReset}
            style={{
              background: '#f8fafc',
              color: '#7c3aed',
              border: '1px solid #7c3aed',
              padding: '8px 12px',
              borderRadius: '8px',
              fontSize: '0.75rem',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.15s'
            }}
          >
            <RefreshCw size={14} /> Resetar Painel
          </button>
        )}

        {status === 'running' || status === 'idle' ? (
          <button
            onClick={handleStopContainers}
            style={{
              background: '#fff7ed',
              color: '#c2410c',
              border: '1px solid #ffedd5',
              padding: '8px 12px',
              borderRadius: '8px',
              fontSize: '0.75rem',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.15s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#ffedd5'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#fff7ed'}
          >
            <Power size={14} /> Encerrar Contêineres
          </button>
        ) : status === 'stopping' ? (
          <button
            disabled
            style={{
              background: '#fff7ed',
              color: '#c2410c',
              border: '1px solid #ffedd5',
              padding: '8px 12px',
              borderRadius: '8px',
              fontSize: '0.75rem',
              fontWeight: 700,
              cursor: 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <Loader2 size={14} className="spin" /> Encerrando seguro...
          </button>
        ) : null}
      </div>

      {/* Auto-Shutdown Toggle Switch */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 12px',
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '8px'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569' }}>Desativar Automático</span>
          <span style={{ fontSize: '0.58rem', color: '#94a3b8' }}>Parar containers pós-fim</span>
        </div>
        <label className="switch" style={{
          position: 'relative',
          display: 'inline-block',
          width: '34px',
          height: '20px'
        }}>
          <input 
            type="checkbox" 
            checked={autoShutdown} 
            onChange={(e) => setAutoShutdown(e.target.checked)}
            style={{ opacity: 0, width: 0, height: 0 }}
          />
          <span className="slider" style={{
            position: 'absolute',
            cursor: 'pointer',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: autoShutdown ? '#7c3aed' : '#cbd5e1',
            transition: '0.3s',
            borderRadius: '10px'
          }}>
            <span style={{
              position: 'absolute',
              content: '""',
              height: '14px', width: '14px',
              left: autoShutdown ? '17px' : '3px',
              bottom: '3px',
              backgroundColor: '#white',
              background: '#ffffff',
              transition: '0.3s',
              borderRadius: '50%'
            }} />
          </span>
        </label>
      </div>

      {/* Active Services List ("Serviços em Execução") */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        padding: '10px 12px'
      }}>
        <span style={{ fontSize: '0.65rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Serviços & Contêineres
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {[
            { name: 'PostgreSQL Database', active: status !== 'stopped' },
            { name: 'Redis Cache Server', active: status !== 'stopped' },
            { name: 'NestJS backend API', active: status !== 'stopped' },
            { name: 'Playwright Chrome browser', active: status === 'running' },
            { name: 'Alice Voice TTS Engine', active: status === 'running' }
          ].map((srv, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.65rem' }}>
              <span style={{ color: '#475569', fontWeight: 500 }}>{srv.name}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ 
                  width: '6px', 
                  height: '6px', 
                  borderRadius: '50%', 
                  background: srv.active ? '#10b981' : '#94a3b8' 
                }} />
                <span style={{ color: srv.active ? '#10b981' : '#94a3b8', fontWeight: 600, fontSize: '0.6rem' }}>
                  {srv.active ? 'Ativo' : 'Inativo'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
